import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from '@e965/xlsx';
import { useLanguage } from '../contexts/LanguageContext';
import { EngineeringLoadingScreen } from '../components/EngineeringLoadingScreen';
import UserProfile from '../components/UserProfile';
import MaterialDropdown from '../components/MaterialDropdown';
import { getUserProfile, checkManagerSubscriptionStatus, getCompanyProfile } from '../services/authService';
import { getCompanyProfileCached } from '../services/cacheService';
import { auth } from '../config/firebase';
import { Language } from '../constants/strings';
import { logger } from '../utils/logger';
import { extractExcelFromPdf } from '../services/pdfAttachmentService';
import { loadSavedPdf, clearSavedPdf } from '../utils/savedPdfStorage';
import {
    listRevisions,
    hasConfiguredLocalServer,
    getStorageMode,
    getDetailWorkspaceStatus,
    RevisionListItem,
    LocalServerError
} from '../services/localServerService';
import {
    listCloudRevisionsForDetail,
    getCloudDetailWorkspaceStatus,
    ensureCloudRetentionIfDue
} from '../services/cloudStorageService';
import type { PdfWorkflowMode } from '../services/authService';
import '../../public/styles/main.css';

const OPERATOR_REVISION_BY_DETAIL_LS_KEY = 'cachedOperatorRevisionByDetail';

/** Last revision id picked by operator, keyed by trimmed detail number. */
function readOperatorRevisionMap(): Record<string, string> {
    try {
        const raw = localStorage.getItem(OPERATOR_REVISION_BY_DETAIL_LS_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as unknown;
        if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
            const out: Record<string, string> = {};
            for (const [ k, v ] of Object.entries(parsed as Record<string, unknown>)) {
                if (typeof v === 'string' && v.trim()) out[ k ] = v;
            }
            return out;
        }
    } catch {
        // ignore corrupt storage
    }
    return {};
}

function rememberOperatorRevisionForDetail(detailNo: string, revisionId: string) {
    if (!detailNo.trim() || !revisionId) return;
    try {
        const map = readOperatorRevisionMap();
        map[ detailNo.trim() ] = revisionId;
        localStorage.setItem(OPERATOR_REVISION_BY_DETAIL_LS_KEY, JSON.stringify(map));
    } catch {
        // ignore quota / private mode
    }
}

interface HomePageProps {
    onLogout: () => void;
}

export default function HomePage({ onLogout }: HomePageProps) {
    const { strings, setLanguage } = useLanguage();
    const navigate = useNavigate();
    const [ detail, setDetail ] = useState(() => localStorage.getItem('cachedDetail') ?? '');
    const [ order, setOrder ] = useState(() => localStorage.getItem('cachedOrder') ?? '');
    const [ density, setDensity ] = useState('');
    // Measurement report number (MDN): from embedded Excel in PDF, not user input
    const [ counter, setCounter ] = useState<string>('');
    const [ pdfFile, setPdfFile ] = useState<File | null>(null);
    const [ excelFile, setExcelFile ] = useState<File | null>(null);
    const [ pdfError, setPdfError ] = useState('');
    const [ excelError, setExcelError ] = useState('');
    const [ selectedRole, setSelectedRole ] = useState<'user' | 'editor' | null>(null);
    const [ userFirebaseRole, setUserFirebaseRole ] = useState<string>('');
    const [ userDisplayName, setUserDisplayName ] = useState<string>('');
    const [ isLoadingRole, setIsLoadingRole ] = useState(true);
    const [ userOrganization, setUserOrganization ] = useState<string | null>(null);
    const [ excelSheetData, setExcelSheetData ] = useState<{ sheetNames: string[], measurementDensities: string[] }>({ sheetNames: [], measurementDensities: [] });
    const [ editorExcelFile, setEditorExcelFile ] = useState<File | null>(null);
    const [ editorExcelError, setEditorExcelError ] = useState('');
    const [ revision, setRevision ] = useState('');
    const [ isLoadingPdfExtract, setIsLoadingPdfExtract ] = useState(false);
    const [ hasActiveSub, setHasActiveSub ] = useState<boolean>(false);
    const [ subscriptionStatus, setSubscriptionStatus ] = useState<string>('');
    const pdfFileInputRef = useRef<HTMLInputElement>(null);
    // Bumps when a new PDF/embedded settings load starts; stale async callbacks must not update revision/UI.
    const embeddedExcelLoadIdRef = useRef(0);

    // ----- Operator (user) flow against the local server -----
    // hasLocalServer is undefined while we are still resolving company profile.
    const [ hasLocalServer, setHasLocalServer ] = useState<boolean | undefined>(undefined);
    const [ storageMode, setStorageMode ] = useState<PdfWorkflowMode>('manual');
    const [ revisions, setRevisions ] = useState<RevisionListItem[]>([]);
    const [ selectedRevisionId, setSelectedRevisionId ] = useState<string>('');
    const [ isLoadingRevisions, setIsLoadingRevisions ] = useState(false);
    const [ localServerError, setLocalServerError ] = useState<string>('');
    // Bumps on every detail change so stale revision responses are ignored.
    const revisionLookupIdRef = useRef(0);
    const [ editorDetailWorkspace, setEditorDetailWorkspace ] = useState<{
        registered: boolean;
    } | null>(null);
    const [ isLoadingEditorWorkspace, setIsLoadingEditorWorkspace ] = useState(false);
    const editorWorkspaceLookupIdRef = useRef(0);

    const isStorageConfigReady = hasLocalServer !== undefined;

    const hasRemoteStorage = useMemo(
        () => isStorageConfigReady && (hasLocalServer === true || storageMode === 'cloudDb'),
        [ isStorageConfigReady, hasLocalServer, storageMode ]
    );

    const showEditorPdfUpload = useMemo(() => {
        if (selectedRole !== 'editor' || !isStorageConfigReady) return false;
        if (!hasRemoteStorage) return true;
        if (isLoadingEditorWorkspace || editorDetailWorkspace === null) return false;
        return !editorDetailWorkspace.registered;
    }, [
        selectedRole,
        isStorageConfigReady,
        hasRemoteStorage,
        isLoadingEditorWorkspace,
        editorDetailWorkspace
    ]);

    const showOperatorPdfUpload =
        selectedRole === 'user' && isStorageConfigReady && !hasRemoteStorage;

    const showEditorRevisionInput = useMemo(() => {
        if (selectedRole !== 'editor') return false;
        if (!hasRemoteStorage) return true;
        if (isLoadingEditorWorkspace || editorDetailWorkspace === null) return false;
        if (editorDetailWorkspace.registered) {
            if (isLoadingRevisions) return false;
            return !selectedRevisionId;
        }
        return true;
    }, [
        selectedRole,
        hasRemoteStorage,
        isLoadingEditorWorkspace,
        editorDetailWorkspace,
        isLoadingRevisions,
        selectedRevisionId
    ]);

    /**
     * Editor + new revision flow: prevent the editor from typing a revision label
     * that already exists on the server for this detail. Comparison is
     * case-insensitive on the trimmed value. Skipped when the editor is editing
     * an existing revision (selectedRevisionId set), in manual mode, or when no
     * registered detail exists yet.
     */
    const isRevisionDuplicate = useMemo(() => {
        if (selectedRole !== 'editor') return false;
        if (!hasRemoteStorage) return false;
        if (selectedRevisionId) return false;
        if (!editorDetailWorkspace?.registered) return false;
        const trimmed = revision.trim().toLowerCase();
        if (!trimmed) return false;
        return revisions.some(r => r.revision.trim().toLowerCase() === trimmed);
    }, [ selectedRole, hasRemoteStorage, selectedRevisionId, editorDetailWorkspace, revision, revisions ]);

    const editorRevisionOptions = useMemo(
        () => [
            { value: '', label: strings.HOME_EDITOR_REVISION_UPLOAD_NEW },
            ...revisions.map(r => ({ value: r.id, label: r.revision }))
        ],
        [ revisions, strings.HOME_EDITOR_REVISION_UPLOAD_NEW ]
    );

    const operatorRevisionOptions = useMemo(
        () => revisions.map(r => ({ value: r.id, label: r.revision })),
        [ revisions ]
    );

    const operatorRevisionPlaceholder = useMemo(() => {
        if (isLoadingRevisions) {
            return strings.LOADING;
        }
        if (revisions.length === 0 && detail.trim()) {
            return strings.LOCAL_SERVER_NO_REVISIONS;
        }
        return '—';
    }, [ isLoadingRevisions, revisions.length, detail, strings.LOADING, strings.LOCAL_SERVER_NO_REVISIONS ]);

    // Load user language from profile on component mount
    useEffect(() => {
        const loadUserLanguage = async () => {
            const currentUser = auth.currentUser;
            if (currentUser) {
                try {
                    const userProfile = await getUserProfile(currentUser.uid);
                    if (userProfile && userProfile.language) {
                        const userLanguage = userProfile.language as Language;
                        if (userLanguage === 'en' || userLanguage === 'et' || userLanguage === 'ru') {
                            setLanguage(userLanguage);
                            logger.log('[HomePage] Language set from user profile:', userLanguage);
                        }
                    }
                } catch (error) {
                    logger.error('[HomePage] Error loading user language:', error);
                }
            }
        };
        loadUserLanguage();
    }, [ setLanguage ]);

    // Load user role from Firebase on component mount
    useEffect(() => {
        let mounted = true;

        const loadUserRole = async () => {
            try {
                // Wait a bit for auth state to settle
                await new Promise(resolve => setTimeout(resolve, 100));

                if (!mounted) return;

                const currentUser = auth.currentUser;
                if (currentUser) {
                    const userProfile = await getUserProfile(currentUser.uid);

                    if (!mounted) return;

                    if (userProfile && userProfile.role) {
                        // Store original Firebase role and display name
                        setUserFirebaseRole(userProfile.role);
                        setUserDisplayName(userProfile.displayName || currentUser.displayName || 'User');

                        // Load company name from CompanyProfile
                        // organization field stores companyId, not company name
                        let companyName: string | null = null;
                        if (userProfile.organization) {
                            try {
                                const companyProfile = await getCompanyProfile(userProfile.organization);
                                if (companyProfile) {
                                    companyName = companyProfile.companyName || null;
                                }
                            } catch (error) {
                                logger.warn('[HomePage] Could not load company profile:', error);
                            }
                        }
                        setUserOrganization(companyName);

                        // Map Firebase role to component role
                        const role = userProfile.role.toLowerCase();

                        // Only allow user and editor roles on home page
                        if (role === 'user') {
                            setSelectedRole('user');
                            logger.log('User role detected - user mode');

                            // Check manager subscription status
                            const subStatus = await checkManagerSubscriptionStatus(currentUser.uid);
                            if (mounted) {
                                setHasActiveSub(subStatus.hasActiveSubscription);
                                setSubscriptionStatus(subStatus.subscriptionStatus || '');
                            }

                            setIsLoadingRole(false);
                        } else if (role === 'editor') {
                            setSelectedRole('editor');
                            logger.log('Editor role detected - editor mode');

                            // Check manager subscription status
                            const subStatus = await checkManagerSubscriptionStatus(currentUser.uid);
                            if (mounted) {
                                setHasActiveSub(subStatus.hasActiveSubscription);
                                setSubscriptionStatus(subStatus.subscriptionStatus || '');
                            }

                            setIsLoadingRole(false);
                        } else if (role === 'admin' || role === 'manager') {
                            // Redirect admin/manager to dashboard
                            logger.log('Admin/Manager detected - redirecting to dashboard');
                            navigate('/dashboard', { replace: true });
                        } else {
                            logger.warn('Unknown role from Firebase:', userProfile.role);
                            navigate('/login', { replace: true });
                        }
                    } else {
                        logger.warn('No user profile found');
                        navigate('/login', { replace: true });
                    }
                } else {
                    logger.warn('No current user found');
                    navigate('/login', { replace: true });
                }
            } catch (error) {
                logger.error('Error loading user role from Firebase:', error);
                if (mounted) {
                    navigate('/login', { replace: true });
                }
            }
        };

        loadUserRole();

        // Cleanup function
        return () => {
            mounted = false;
        };
    }, [ navigate ]);

    // Save detail value to localStorage when it changes
    useEffect(() => {
        if (detail) {
            localStorage.setItem('cachedDetail', detail);
        }
    }, [ detail ]);

    // Save order value to localStorage when it changes
    useEffect(() => {
        if (order) {
            localStorage.setItem('cachedOrder', order);
        }
    }, [ order ]);

    // Detect whether the current company has a local server configured.
    // While undefined the user-role UI shows a small loading hint instead of the
    // legacy PDF picker, to avoid flashing the wrong UX.
    useEffect(() => {
        if (selectedRole !== 'user' && selectedRole !== 'editor') {
            setHasLocalServer(undefined);
            setStorageMode('manual');
            return;
        }
        let cancelled = false;
        void Promise.all([ hasConfiguredLocalServer(), getStorageMode() ])
            .then(([ ls, mode ]) => {
                if (!cancelled) {
                    setHasLocalServer(ls);
                    setStorageMode(mode);
                }
            })
            .catch((err) => {
                logger.warn('[HomePage] Failed to resolve storage config', err);
                if (!cancelled) {
                    setHasLocalServer(false);
                    setStorageMode('manual');
                }
            });
        return () => { cancelled = true; };
    }, [ selectedRole ]);

    useEffect(() => {
        if (storageMode !== 'cloudDb') return;
        let cancelled = false;
        void ensureCloudRetentionIfDue().catch((err) => {
            if (!cancelled) {
                logger.warn('[HomePage] cloud weekly retention failed', err);
            }
        });
        return () => { cancelled = true; };
    }, [ storageMode ]);

    // Drop "no embedded settings" home error when company uses remote storage.
    useEffect(() => {
        if (hasRemoteStorage && (selectedRole === 'user' || selectedRole === 'editor')) {
            setPdfError('');
        }
    }, [ hasRemoteStorage, selectedRole ]);

    // Editor + remote storage: whether this detail is already registered on server/cloud.
    useEffect(() => {
        if (selectedRole !== 'editor' || !hasRemoteStorage) {
            setEditorDetailWorkspace(null);
            setIsLoadingEditorWorkspace(false);
            return;
        }
        const trimmed = detail.trim();
        if (!trimmed) {
            setEditorDetailWorkspace(null);
            setIsLoadingEditorWorkspace(false);
            return;
        }
        const lookupId = ++editorWorkspaceLookupIdRef.current;
        setIsLoadingEditorWorkspace(true);
        const timer = setTimeout(() => {
            const loadStatus = storageMode === 'cloudDb'
                ? getCloudDetailWorkspaceStatus(trimmed)
                : getDetailWorkspaceStatus(trimmed);
            void loadStatus
                .then((s) => {
                    if (lookupId !== editorWorkspaceLookupIdRef.current) return;
                    setEditorDetailWorkspace(s);
                    setIsLoadingEditorWorkspace(false);
                })
                .catch((err: unknown) => {
                    if (lookupId !== editorWorkspaceLookupIdRef.current) return;
                    logger.warn('[HomePage] Failed to load detail workspace status', err);
                    setEditorDetailWorkspace({ registered: false });
                    setIsLoadingEditorWorkspace(false);
                });
        }, 300);
        return () => clearTimeout(timer);
    }, [ selectedRole, hasRemoteStorage, storageMode, detail ]);

    // Remote storage: never keep a locally cached PDF — drawings come from server/cloud on demand.
    useEffect(() => {
        if (!hasRemoteStorage) return;
        setPdfFile(null);
        setExcelFile(null);
        setEditorExcelFile(null);
        setPdfError('');
        void clearSavedPdf();
    }, [ hasRemoteStorage ]);

    // Load saved PDF on component mount and extract embedded settings.
    // Skipped while remote-storage flag is unknown or enabled — operators/editors then
    // use server-driven PDFs; only when hasRemoteStorage === false do we restore a
    // saved PDF and require embedded settings.
    useEffect(() => {
        if ((selectedRole === 'user' || selectedRole === 'editor') && hasRemoteStorage !== false) return;

        const loadSavedFiles = async () => {
            const loadId = ++embeddedExcelLoadIdRef.current;
            const savedPdf = await loadSavedPdf();

            if (savedPdf) {
                try {
                    setPdfFile(savedPdf);
                    setPdfError('');

                    const extracted = await extractExcelFromPdf(savedPdf);
                    if (loadId !== embeddedExcelLoadIdRef.current) return;

                    if (selectedRole === 'user') {
                        if (extracted) {
                            setExcelFile(extracted);
                            setExcelError('');
                            processExcelFile(extracted, loadId);
                        } else {
                            setPdfError(strings.PDF_NO_EMBEDDED_SETTINGS);
                            setExcelFile(null);
                            setRevision('');
                            setCounter('');
                        }
                    } else if (selectedRole === 'editor') {
                        if (extracted) {
                            setEditorExcelFile(extracted);
                            setEditorExcelError('');
                            processEditorExcelFile(extracted, loadId);
                        } else {
                            setEditorExcelFile(null);
                            setRevision('');
                        }
                    }
                } catch (error) {
                    logger.error('[HomePage] Error loading saved PDF:', error);
                }
            }
        };

        loadSavedFiles();
    }, [ selectedRole, hasRemoteStorage ]);

    // Load revisions whenever the detail number changes (operator + editor, remote storage only).
    useEffect(() => {
        if ((selectedRole !== 'user' && selectedRole !== 'editor') || !hasRemoteStorage) {
            setRevisions([]);
            setSelectedRevisionId('');
            return;
        }
        const trimmed = detail.trim();
        if (!trimmed) {
            setRevisions([]);
            setSelectedRevisionId('');
            return;
        }
        if (selectedRole === 'editor') {
            if (editorDetailWorkspace === null || isLoadingEditorWorkspace) {
                setRevisions([]);
                setSelectedRevisionId('');
                setIsLoadingRevisions(true);
                return;
            }
            if (!editorDetailWorkspace.registered) {
                setRevisions([]);
                setSelectedRevisionId('');
                setIsLoadingRevisions(false);
                return;
            }
        }
        const lookupId = ++revisionLookupIdRef.current;
        setIsLoadingRevisions(true);
        setLocalServerError('');
        const timer = setTimeout(() => {
            const loadRevisions = storageMode === 'cloudDb'
                ? listCloudRevisionsForDetail(trimmed)
                : listRevisions(trimmed, 'published');
            void loadRevisions
                .then((items) => {
                    if (lookupId !== revisionLookupIdRef.current) return;
                    setRevisions(items);
                    if (selectedRole === 'user') {
                        const pref = readOperatorRevisionMap()[ trimmed ];
                        const usePref = pref && items.some((r) => r.id === pref);
                        setSelectedRevisionId(usePref ? pref : items[0]?.id ?? '');
                    } else {
                        setSelectedRevisionId(items[0]?.id ?? '');
                    }
                    setIsLoadingRevisions(false);
                })
                .catch((err: unknown) => {
                    if (lookupId !== revisionLookupIdRef.current) return;
                    setRevisions([]);
                    setSelectedRevisionId('');
                    setIsLoadingRevisions(false);
                    if (err instanceof LocalServerError && err.status !== 404) {
                        setLocalServerError(err.message);
                    }
                });
        }, 300);
        return () => clearTimeout(timer);
    }, [ selectedRole, hasRemoteStorage, storageMode, detail, editorDetailWorkspace, isLoadingEditorWorkspace ]);

    const handlePdfFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[ 0 ]) {
            const selectedFile = e.target.files[ 0 ];
            if (selectedFile.type !== 'application/pdf') {
                setPdfFile(null);
                setPdfError(strings.INVALID_PDF_FILE);
            } else {
                const loadId = ++embeddedExcelLoadIdRef.current;
                setPdfFile(selectedFile);
                setPdfError('');
                setExcelFile(null);
                setEditorExcelFile(null);
                setExcelError('');
                setEditorExcelError('');
                setCounter('');
                setIsLoadingPdfExtract(true);
                try {
                    const extracted = await extractExcelFromPdf(selectedFile);
                    if (loadId !== embeddedExcelLoadIdRef.current) return;

                    if (selectedRole === 'user') {
                        if (extracted) {
                            setExcelFile(extracted);
                            processExcelFile(extracted, loadId);
                        } else {
                            if (!hasRemoteStorage) {
                                setPdfError(strings.PDF_NO_EMBEDDED_SETTINGS);
                            }
                            setExcelFile(null);
                            setRevision('');
                            setCounter('');
                        }
                    } else if (selectedRole === 'editor') {
                        if (hasRemoteStorage) {
                            setEditorExcelFile(null);
                            localStorage.removeItem('editorExcelRects');
                            localStorage.removeItem('editorExcelRefDimensions');
                        } else if (extracted) {
                            setEditorExcelFile(extracted);
                            processEditorExcelFile(extracted, loadId);
                        } else {
                            setRevision('');
                        }
                    }
                } catch (error) {
                    logger.error('[HomePage] Error extracting settings from PDF:', error);
                    if (selectedRole === 'user' && !hasRemoteStorage) {
                        setPdfError(strings.PDF_NO_EMBEDDED_SETTINGS);
                    }
                    if (loadId === embeddedExcelLoadIdRef.current) {
                        setRevision('');
                    }
                } finally {
                    setIsLoadingPdfExtract(false);
                }
            }
        }
    };

    const processEditorExcelFile = (file: File, loadId: number) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[ 0 ];
                const worksheet = workbook.Sheets[ firstSheetName ];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                // Check tool count in settings file for editor role
                const currentUser = auth.currentUser;
                if (currentUser) {
                    try {
                        const companyProfile = await getCompanyProfileCached(currentUser.uid);
                        if (loadId !== embeddedExcelLoadIdRef.current) return;

                        if (companyProfile) {
                            const tools = companyProfile.tools || [];
                            const companyActiveToolCount = tools.length;

                            // Find max tool number in settings file
                            let maxToolInSettings = 0;
                            jsonData.forEach((row: any) => {
                                if (row[ 'Tool' ]) {
                                    const toolNumber = parseInt(row[ 'Tool' ], 10);
                                    if (!isNaN(toolNumber) && toolNumber > maxToolInSettings) {
                                        maxToolInSettings = toolNumber;
                                    }
                                }
                            });

                            // Validate that max tool number doesn't exceed company active tools count
                            // User can load settings file with fewer tools, but tool numbers must be within valid range (1 to companyActiveToolCount)
                            if (maxToolInSettings > 0 && maxToolInSettings > companyActiveToolCount) {
                                if (loadId !== embeddedExcelLoadIdRef.current) return;
                                setEditorExcelFile(null);
                                setEditorExcelError(strings.PDFVIEWER_SETTINGS_TOOL_COUNT_MISMATCH);
                                return;
                            }
                        }
                    } catch (error) {
                        logger.error('Error checking company tools for settings file validation:', error);
                    }
                }

                if (loadId !== embeddedExcelLoadIdRef.current) return;

                // Clear error if validation passes
                setEditorExcelError('');

                // Check for reference dimensions and revision in Excel file
                const firstRow = jsonData[ 0 ] as any;
                let excelRefDimensions = null;
                const revisionFromFile = firstRow && (firstRow[ 'Revision' ] || firstRow[ 'Revisjon' ] || firstRow[ 'Ревизия' ]);
                if (revisionFromFile != null && String(revisionFromFile).trim() !== '') {
                    setRevision(String(revisionFromFile).trim());
                } else {
                    setRevision('');
                }

                if (firstRow && firstRow[ 'Ref. Width' ]) {
                    excelRefDimensions = {
                        width: parseFloat(firstRow[ 'Ref. Width' ] || '800'),
                        height: parseFloat(firstRow[ 'Ref. Height' ] || '600')
                    };
                }

                // Filter and create rectangles from Excel data (English-only headers)
                const validRows = jsonData.filter((row: any) => {
                    const hasMeasurementNr = row[ 'M. Nr' ] != null && row[ 'M. Nr' ] !== '';
                    const hasPosition = row[ 'X' ] != null && row[ 'Y' ] != null;
                    const hasDimensions = row[ 'Width' ] != null && row[ 'Height' ] != null;
                    return hasMeasurementNr && hasPosition && hasDimensions;
                });

                const newRects = validRows.map((row: any, index: number) => {
                    const measurementNr = parseInt(row[ 'M. Nr' ], 10);
                    let x = parseFloat(row[ 'X' ] || 0);
                    let y = parseFloat(row[ 'Y' ] || 0);
                    let width = parseFloat(row[ 'Width' ] || 50);
                    let height = parseFloat(row[ 'Height' ] || 50);

                    if (isNaN(measurementNr) || isNaN(x) || isNaN(y) || isNaN(width) || isNaN(height)) {
                        return null;
                    }

                    // Use M. Nr as both measurementNr and rect.number
                    return {
                        x,
                        y,
                        width,
                        height,
                        number: measurementNr,
                        measurementNr: measurementNr,
                        text: '',
                        enteredNumber: row[ 'Tool' ] ? parseInt(row[ 'Tool' ], 10) : undefined,
                        tolerancePlus: row[ 'Upper' ] || '',
                        toleranceMinus: row[ 'Lower' ] || '',
                        nominalDimension: row[ 'Nominal Dim.' ] || '',
                        toleranceType: (row[ 'Type' ] as 'plusminus' | 'plus' | 'minus' | undefined) || undefined,
                    };
                }).filter(rect => rect !== null);

                // Store the processed rectangles for PdfViewer
                if (newRects.length > 0) {
                    if (loadId !== embeddedExcelLoadIdRef.current) return;
                    localStorage.setItem('editorExcelRects', JSON.stringify(newRects));
                    localStorage.setItem('editorExcelRefDimensions', JSON.stringify(excelRefDimensions));
                }
            } catch (error) {
                if (loadId === embeddedExcelLoadIdRef.current) {
                    setEditorExcelError(strings.HOME_EXCEL_ERROR);
                }
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const processExcelFile = (file: File, loadId: number) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });

                // Check tool count in settings file for operator role
                if (selectedRole === 'user') {
                    const currentUser = auth.currentUser;
                    if (currentUser) {
                        try {
                            const companyProfile = await getCompanyProfileCached(currentUser.uid);
                            if (loadId !== embeddedExcelLoadIdRef.current) return;

                            if (companyProfile) {
                                const tools = companyProfile.tools || [];
                                const companyActiveToolCount = tools.length;

                                // Check first sheet for tool numbers (settings file structure)
                                const firstSheetName = workbook.SheetNames[ 0 ];
                                const worksheet = workbook.Sheets[ firstSheetName ];
                                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                                // Find max tool number in settings file
                                let maxToolInSettings = 0;
                                jsonData.forEach((row: any) => {
                                    if (row[ 'Tool' ]) {
                                        const toolNumber = parseInt(row[ 'Tool' ], 10);
                                        if (!isNaN(toolNumber) && toolNumber > maxToolInSettings) {
                                            maxToolInSettings = toolNumber;
                                        }
                                    }
                                });

                                // Validate that max tool number doesn't exceed company active tools count
                                // User can load settings file with fewer tools, but tool numbers must be within valid range (1 to companyActiveToolCount)
                                if (maxToolInSettings > 0 && maxToolInSettings > companyActiveToolCount) {
                                    if (loadId !== embeddedExcelLoadIdRef.current) return;
                                    setExcelFile(null);
                                    setCounter('');
                                    setExcelError(strings.PDFVIEWER_SETTINGS_TOOL_COUNT_MISMATCH);
                                    setExcelSheetData({ sheetNames: [], measurementDensities: [] });
                                    return;
                                }
                            }
                        } catch (error) {
                            logger.error('Error checking company tools for settings file validation:', error);
                        }
                    }
                }

                if (loadId !== embeddedExcelLoadIdRef.current) return;

                if (selectedRole === 'user') {
                    const firstSheetName = workbook.SheetNames[ 0 ];
                    if (firstSheetName) {
                        const worksheet = workbook.Sheets[ firstSheetName ];
                        const jsonData = XLSX.utils.sheet_to_json(worksheet);
                        const firstRow = jsonData[ 0 ] as any;
                        const revisionFromFile = firstRow && (firstRow[ 'Revision' ] || firstRow[ 'Revisjon' ] || firstRow[ 'Ревизия' ]);
                        if (revisionFromFile != null && String(revisionFromFile).trim() !== '') {
                            setRevision(String(revisionFromFile).trim());
                        } else {
                            setRevision('');
                        }
                        const mdnFromFile = firstRow && (
                            firstRow[ 'MDN' ] ??
                            firstRow[ 'MeasurementReportNumber' ] ??
                            firstRow[ 'Measurement Report Number' ] ??
                            firstRow[ 'MeasurementReportNo' ] ??
                            firstRow[ 'ReportNumber' ]
                        );
                        if (mdnFromFile != null && String(mdnFromFile).trim() !== '') {
                            setCounter(String(mdnFromFile).trim());
                        } else {
                            setCounter('');
                        }
                    } else {
                        setRevision('');
                        setCounter('');
                    }
                }

                // Clear error if validation passes
                setExcelError('');

                // Extract sheet names
                const sheetNames = workbook.SheetNames;

                // Extract measurement densities from all sheets
                const measurementDensities: string[] = [];
                const uniqueDensities = new Set<string>();

                sheetNames.forEach(sheetName => {
                    const worksheet = workbook.Sheets[ sheetName ];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet);

                    jsonData.forEach((row: any) => {
                        // Look for measurement density in various possible column names
                        const density = row[ 'Mõõtmistihedus' ] ||
                            row[ 'Measurement Density' ] ||
                            row[ 'Tihedus' ] ||
                            row[ 'Density' ];

                        if (density && typeof density === 'string' && density.trim() !== '') {
                            uniqueDensities.add(density.trim());
                        }
                    });
                });

                // Sort densities numerically
                if (loadId !== embeddedExcelLoadIdRef.current) return;
                setExcelSheetData({
                    sheetNames: sheetNames,
                    measurementDensities: Array.from(uniqueDensities)
                        .sort((a, b) => {
                            const na = parseFloat(a.replace(',', '.'));
                            const nb = parseFloat(b.replace(',', '.'));
                            if (!isNaN(na) && !isNaN(nb)) return na - nb;
                            if (!isNaN(na)) return -1;
                            if (!isNaN(nb)) return 1;
                            return a.localeCompare(b, 'et');
                        })
                });

            } catch (error) {
                if (loadId === embeddedExcelLoadIdRef.current) {
                    setExcelSheetData({ sheetNames: [], measurementDensities: [] });
                }
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Defense-in-depth: editor + new revision must not collide with an
        // existing revision label on the server. The Continue button is
        // already disabled in that case; this guards the keyboard-Enter path.
        if (isRevisionDuplicate) {
            return;
        }

        if (selectedRole === 'editor' && hasRemoteStorage && detail.trim() && selectedRevisionId) {
            const editorReportLanguage = localStorage.getItem('reportLanguage') as 'et' | 'en' | 'ru' || 'et';
            setEditorExcelError('');
            navigate('/pdf-viewer', {
                state: {
                    detailNo: detail.trim(),
                    revisionId: selectedRevisionId,
                    detail,
                    order: undefined,
                    density,
                    counter,
                    role: 'editor',
                    excelFile: null,
                    editorExcelFile: null,
                    engineerName: userDisplayName,
                    revision: revisions.find(r => r.id === selectedRevisionId)?.revision ?? '',
                    reportLanguage: editorReportLanguage,
                }
            });
            return;
        }

        if (
            selectedRole === 'editor'
            && hasRemoteStorage
            && detail.trim()
            && !selectedRevisionId
            && editorDetailWorkspace?.registered
            && revision.trim()
        ) {
            const pdfSourceRevision = revisions.length > 0 ? revisions[ revisions.length - 1 ] : null;
            if (!pdfSourceRevision) {
                return;
            }
            const editorReportLanguage = localStorage.getItem('reportLanguage') as 'et' | 'en' | 'ru' || 'et';
            setEditorExcelError('');
            navigate('/pdf-viewer', {
                state: {
                    detailNo: detail.trim(),
                    revisionId: pdfSourceRevision.id,
                    detail,
                    order: undefined,
                    density,
                    counter,
                    role: 'editor',
                    excelFile: null,
                    editorExcelFile: null,
                    engineerName: userDisplayName,
                    revision: revision.trim(),
                    reportLanguage: editorReportLanguage,
                    isNewEditorRevision: true,
                }
            });
            return;
        }

        if (selectedRole === 'editor' && pdfFile && !pdfError) {
            if (!editorExcelFile && (!revision || !revision.trim())) {
                setEditorExcelError(strings.REVISION_REQUIRED);
                return;
            }
            setEditorExcelError('');
            const editorReportLanguage = localStorage.getItem('reportLanguage') as 'et' | 'en' | 'ru' || 'et';
            navigate('/pdf-viewer', {
                state: {
                    pdfFile,
                    detail,
                    order: undefined,
                    density,
                    counter,
                    role: selectedRole,
                    excelFile: null,
                    editorExcelFile: hasRemoteStorage ? null : editorExcelFile,
                    engineerName: userDisplayName,
                    revision,
                    reportLanguage: editorReportLanguage,
                    isNewEditorRevision: hasRemoteStorage,
                }
            });
        } else if (selectedRole === 'user' && hasRemoteStorage && detail && selectedRevisionId && order) {
            rememberOperatorRevisionForDetail(detail.trim(), selectedRevisionId);
            // Operator (server/cloud-driven) path: PdfViewerPage fetches PDF + settings
            // using detailNo + revisionId.
            const reportLanguage = localStorage.getItem('reportLanguage') as 'et' | 'en' | 'ru' || 'et';
            navigate('/pdf-viewer', {
                state: {
                    detailNo: detail.trim(),
                    revisionId: selectedRevisionId,
                    detail,
                    order,
                    density,
                    role: selectedRole,
                    engineerName: userDisplayName,
                    reportLanguage,
                }
            });
        } else if (selectedRole === 'user' && !hasRemoteStorage && pdfFile && excelFile && !pdfError && !excelError && order) {
            // Legacy fallback path (remote storage not configured for this company).
            const reportLanguage = localStorage.getItem('reportLanguage') as 'et' | 'en' | 'ru' || 'et';
            navigate('/pdf-viewer', {
                state: {
                    pdfFile,
                    detail,
                    order,
                    density,
                    counter,
                    role: selectedRole,
                    excelFile,
                    editorExcelFile: null,
                    engineerName: userDisplayName,
                    revision,
                    reportLanguage,
                }
            });
        }
    };

    // Render loading state
    if (isLoadingRole) {
        return <EngineeringLoadingScreen ariaLabel={strings.LOADING} />;
    }

    if ((selectedRole === 'user' || selectedRole === 'editor') && (!userOrganization || !hasActiveSub)) {
        const handleBackToLogin = async () => {
            try {
                // Sign out user before redirecting to login
                await auth.signOut();
                navigate('/login', { replace: true });
            } catch (error) {
                logger.error('[HomePage] Error signing out:', error);
                // Force navigate even if signout fails
                navigate('/login', { replace: true });
            }
        };

        return (
            <div className="no-organization-container">
                <div className="no-organization-message">
                    {strings.NO_ORGANIZATION_MESSAGE}
                </div>
                <button
                    className="back-button"
                    onClick={handleBackToLogin}
                >
                    {strings.BACK_BUTTON}
                </button>
            </div>
        );
    }

    return (
        <>
            <h1 className="dashboard-main-title">QUALITY DATUM</h1>
            <div className="container css-test-loaded">
                <UserProfile displayName={userDisplayName} />
                <button className="logout-btn" onClick={onLogout}>
                    {strings.PROFILE_LOGOUT}
                </button>

                <form className="form home-form-card" onSubmit={handleSubmit}>
                    {(hasLocalServer === true || storageMode === 'cloudDb') && (
                        <button
                            type="button"
                            className="home-form-history-btn"
                            onClick={() => navigate('/reports')}
                            aria-label={strings.REPORTS_NAV_BTN}
                            title={strings.REPORTS_NAV_BTN}
                        >
                            <svg
                                className="home-form-history-icon"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                                focusable="false"
                            >
                                <path
                                    fill="currentColor"
                                    d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"
                                />
                            </svg>
                        </button>
                    )}
                    <h1 className="title">{userOrganization || strings.APP_TITLE}</h1>
                    <label className="label">
                        {strings.DETAIL_NUMBER}
                        <input
                            className="input"
                            type="text"
                            value={detail}
                            onChange={e => setDetail(e.target.value)}
                            required
                            placeholder="Sisesta detaili number"
                        />
                    </label>
                    {selectedRole === 'editor' && hasRemoteStorage && detail.trim() && editorDetailWorkspace?.registered && !isLoadingEditorWorkspace && (
                        <label className="label">
                            {strings.REVISION_LABEL}
                            <MaterialDropdown
                                options={editorRevisionOptions}
                                value={selectedRevisionId}
                                onChange={(v) => {
                                    setSelectedRevisionId(v);
                                    setPdfFile(null);
                                    setPdfError('');
                                    setEditorExcelFile(null);
                                    setEditorExcelError('');
                                    if (!v) {
                                        setRevision('');
                                    }
                                }}
                                disabled={!detail.trim() || isLoadingRevisions || isLoadingEditorWorkspace}
                                placeholder={strings.HOME_EDITOR_REVISION_UPLOAD_NEW}
                            />
                        </label>
                    )}
                    {showEditorRevisionInput && (
                        <label className="label">
                            {strings.REVISION_LABEL}
                            <input
                                className="input"
                                type="text"
                                value={revision}
                                onChange={e => setRevision(e.target.value)}
                                placeholder="Sisesta revisjon"
                            />
                            {isRevisionDuplicate && (
                                <span className="file-error">{strings.HOME_EDITOR_REVISION_ALREADY_EXISTS}</span>
                            )}
                        </label>
                    )}
                    {selectedRole === 'user' && (
                        <label className="label">
                            {strings.ORDER_NUMBER}
                            <input
                                className="input"
                                type="text"
                                value={order}
                                onChange={e => setOrder(e.target.value)}
                                required
                                placeholder="Sisesta tellimuse number"
                            />
                        </label>
                    )}
                    {selectedRole === 'user' && hasRemoteStorage && (
                        <label className="label">
                            {strings.REVISION_LABEL}
                            <MaterialDropdown
                                options={operatorRevisionOptions}
                                value={selectedRevisionId}
                                onChange={(v) => {
                                    setSelectedRevisionId(v);
                                    if (detail.trim() && v) {
                                        rememberOperatorRevisionForDetail(detail.trim(), v);
                                    }
                                }}
                                disabled={!detail.trim() || isLoadingRevisions || revisions.length === 0}
                                placeholder={operatorRevisionPlaceholder}
                            />
                        </label>
                    )}
                    {(showEditorPdfUpload || showOperatorPdfUpload) ? (
                        <div className="file-row">
                            <div>
                                <button
                                    type="button"
                                    className={`file-btn pdf-button${pdfError ? ' file-btn-error' : pdfFile ? ' file-btn-selected' : ''}`}
                                    onClick={() => pdfFileInputRef.current?.click()}
                                >
                                    {pdfFile ? strings.SELECT_NEW : strings.SELECT} <span className="pdf-icon">{strings.PDF}</span>
                                </button>
                                <input
                                    type="file"
                                    accept="application/pdf"
                                    className="hidden"
                                    ref={pdfFileInputRef}
                                    onChange={handlePdfFileChange}
                                />
                                {pdfFile && !pdfError && (
                                    <span className="file-name">
                                        <span className="file-img">{strings.FILE_ICON}</span> {pdfFile.name}
                                    </span>
                                )}
                            </div>
                            {isLoadingPdfExtract && (
                                <span className="file-name">{strings.LOADING}</span>
                            )}
                            {selectedRole === 'user' && excelFile && !pdfError && (
                                <span className="file-name">
                                    <span className="file-img">{strings.FILE_ICON}</span> {strings.PDF_SETTINGS_EXTRACTED}
                                </span>
                            )}
                        </div>
                    ) : null}
                    {(pdfError || excelError || editorExcelError || localServerError) && (
                        <div className="file-error">{pdfError || excelError || editorExcelError || localServerError}</div>
                    )}
                    <button
                        className={`submit-button submit-btn ${
                            (selectedRole === 'editor' && !isStorageConfigReady) ||
                            (selectedRole === 'editor' && hasRemoteStorage && (
                                isLoadingEditorWorkspace
                                || (Boolean(detail.trim()) && editorDetailWorkspace === null)
                                || (editorDetailWorkspace?.registered && isLoadingRevisions && Boolean(detail.trim()))
                                || (editorDetailWorkspace?.registered && selectedRevisionId && !detail.trim())
                                || (editorDetailWorkspace?.registered && !selectedRevisionId && !revision.trim())
                                || (editorDetailWorkspace?.registered && !selectedRevisionId && isRevisionDuplicate)
                                || (editorDetailWorkspace?.registered === false && showEditorPdfUpload && (!pdfFile || !!pdfError || (!editorExcelFile && (!revision || !revision.trim()))))
                            )) ||
                            (selectedRole === 'editor' && isStorageConfigReady && !hasRemoteStorage && (!pdfFile || !!pdfError || (!editorExcelFile && (!revision || !revision.trim())))) ||
                            (selectedRole === 'user' && hasRemoteStorage && (!detail.trim() || !selectedRevisionId || !order)) ||
                            (selectedRole === 'user' && !hasRemoteStorage && (!pdfFile || !excelFile || !!pdfError || !!excelError || !order)) ||
                            (selectedRole === 'user' && !isStorageConfigReady)
                            ? 'btn-disabled' : ''
                            }`}
                        type="submit"
                        disabled={
                            (selectedRole === 'editor' && !isStorageConfigReady) ||
                            (selectedRole === 'editor' && hasRemoteStorage && (
                                isLoadingEditorWorkspace
                                || (Boolean(detail.trim()) && editorDetailWorkspace === null)
                                || (editorDetailWorkspace?.registered && isLoadingRevisions && Boolean(detail.trim()))
                                || (editorDetailWorkspace?.registered && selectedRevisionId && !detail.trim())
                                || (editorDetailWorkspace?.registered && !selectedRevisionId && !revision.trim())
                                || (editorDetailWorkspace?.registered && !selectedRevisionId && isRevisionDuplicate)
                                || (editorDetailWorkspace?.registered === false && showEditorPdfUpload && (!pdfFile || !!pdfError || (!editorExcelFile && (!revision || !revision.trim()))))
                            )) ||
                            (selectedRole === 'editor' && isStorageConfigReady && !hasRemoteStorage && (!pdfFile || !!pdfError || (!editorExcelFile && (!revision || !revision.trim())))) ||
                            (selectedRole === 'user' && hasRemoteStorage && (!detail.trim() || !selectedRevisionId || !order)) ||
                            (selectedRole === 'user' && !hasRemoteStorage && (!pdfFile || !excelFile || !!pdfError || !!excelError || !order)) ||
                            (selectedRole === 'user' && !isStorageConfigReady)
                        }
                    >
                        {strings.CONTINUE} <span className="arrow">{strings.ARROW}</span>
                    </button>
                </form>
            </div>
        </>
    );
}


